import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ControlContainer, FormControl, FormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { CustomValidatorMessage, evaluate, LgdErrorStateMatcher, LgdFormControl, LgdFormControlNumberConfig, } from "../lgdCommon";
import { LgdCurrencyConfig } from "./lgdCurrencyValueAccessor";

@Component({
    selector: "lgd-currency",
    templateUrl: "./lgdCurrency.tmpl.html",
    styleUrls: ["./lgdCurrency.component.scss"],
    standalone: false
})
export class LgdCurrencyComponent implements OnInit, AfterViewInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();
    @ViewChild("input") readonly inputElement: ElementRef<HTMLInputElement>;
    private _initialized: boolean;

    @Input() readonly controlName: string | number;
    @Input() readonly label: string;
    @Input() readonly hideLabel: boolean;
    @Input() readonly tooltip: string;

    readonly errorStateMatcher = new LgdErrorStateMatcher();

    constructor(private readonly controlContainer: ControlContainer) { }

    get initialized(): boolean
    {
        return this._initialized;
    }

    get formGroup(): FormGroup
    {
        return this.controlContainer.control as FormGroup;
    }

    get formControl(): FormControl<string | number>
    {
        if (!this.formGroup || (this.controlName !== undefined && this.controlName == null))
        {
            throw new Error("Cannot access either formGroup or controlName.");
        }

        const controlNameStr = typeof this.controlName === "string" ? this.controlName : `${this.controlName}`;
        return this.formGroup.get(controlNameStr) as FormControl<string | number>;
    }

    get lgdFormControl(): LgdFormControl<LgdFormControlNumberConfig, string | number>
    {
        return LgdFormControl.isLgdFormControl(this.formControl) ? this.formControl : undefined;
    }

    get max(): number
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.max) : 0;
    }

    get min(): number
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.min) : 0;
    }

    get precision(): number
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.precision) : 0;
    }

    get scale(): number
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.scale) : 0;
    }

    private _config: LgdCurrencyConfig;
    get config(): LgdCurrencyConfig
    {
        return (
            this._config ||
            (this._config = {
                precision: this.precision,
                scale: this.scale,
            })
        );
    }

    get digitsInfo(): string
    {
        return this.scale ? `1.2-${this.scale}` : `1.2-2`;
    }

    hasCustomValidators: boolean;
    customErrors: ReadonlyArray<CustomValidatorMessage>;

    ngOnInit(): void
    {
        if (this.lgdFormControl)
        {
            this.hasCustomValidators = !!this.lgdFormControl.data.customValidators;

            this.customErrors = this.hasCustomValidators
                ? this.lgdFormControl.data.customValidators.reduce(
                    (l, x) => l.concat(x.getErrorMessage(this.label)),
                    []
                )
                : [];
        }

        this._initialized = true;
    }

    ngAfterViewInit(): void
    {
        if (!this.inputElement)
        {
            console.warn(`LgdCurrencyComponent.ngAfterViewInit - ViewChild inputElement is not defined!`);
        }

        if (this.lgdFormControl)
        {
            this.lgdFormControl.onFocus
                .pipe(takeUntil(this.destructor$))
                .subscribe((shouldFocus: boolean) =>
                {
                    if (shouldFocus)
                    {
                        /**
                         * Calling in a setTimeout to avoid a bug in our current @angular version.
                         */
                        setTimeout(() =>
                        {
                            this.inputElement.nativeElement.focus();
                        });
                    }
                });
        }
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    hasError(id: string): boolean
    {
        if (this.formControl)
        {
            // TODO - this is the responsibility of the Validators... do not put validation logic here...
            // if (id === "required" && this.isRequired)
            // {
            //     return typeof this.formControl.value === "string"
            //         ? !!this.formControl.value
            //         : isNaN(this.formControl.value);
            // }
            // else
            // {
            return this.formControl.hasError(id);
            // }
        }
        else
        {
            return false;
        }
    }

    showErrorMessage(id: string): boolean
    {
        return this.formControl.touched && this.hasError(id);
    }

    get isInvalid(): boolean
    {
        return this.formControl.touched && this.formControl.invalid;
        // const error =
        //     this.formControl.touched &&
        //     (this.hasError("required") ||
        //         this.hasError("min") ||
        //         this.hasError("max") ||
        //         this.hasError("precision") ||
        //         this.hasError("scale"));

        // return error ? true : null;
    }

    get isRequired(): boolean
    {
        return !!this.lgdFormControl && evaluate(this.lgdFormControl.data.required);
    }

    get helperTextSpacer(): boolean
    {
        return (
            !this.showErrorMessage("required") &&
            !this.showErrorMessage("pattern") &&
            !this.showErrorMessage("min") &&
            !this.showErrorMessage("max") &&
            !this.showErrorMessage("precision") &&
            !this.showErrorMessage("scale") &&
            !this.hasCustomValidators
        );
    }
}
